<div class="modal fade show d-block" tabindex="-1" role="dialog">
  <div class="modal-backdrop fade show" tabindex="0" role="button" aria-label="Close modal" (click)="cancel()" (keyup.enter)="cancel()"></div>
  <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <lucide-icon name="shield" class="text-primary me-2"></lucide-icon>
          {{ (isFirstAuthenticator ? 'profile.enable_2fa_title' : 'profile.authenticators.add_title') | translate }}
        </h5>
        @if (step !== 'recovery') {
          <button type="button" class="btn-close" aria-label="Close" (click)="cancel()" [disabled]="isRequestPending"></button>
        }
      </div>

      <div class="modal-body">
        <!-- Step indicator -->
        <div class="setup-steps mb-4">
          <div class="step" [class.active]="step === 'details'" [class.completed]="step !== 'details'">
            <span class="step-number">1</span>
            <span class="step-label">{{ 'profile.authenticators.step_details' | translate }}</span>
          </div>
          <div class="step-line" [class.completed]="step !== 'details'"></div>
          <div class="step" [class.active]="step === 'scan'" [class.completed]="step === 'recovery'">
            <span class="step-number">2</span>
            <span class="step-label">{{ 'profile.step_scan' | translate }}</span>
          </div>
          @if (isFirstAuthenticator) {
            <div class="step-line" [class.completed]="step === 'recovery'"></div>
            <div class="step" [class.active]="step === 'recovery'">
              <span class="step-number">3</span>
              <span class="step-label">{{ 'profile.step_backup' | translate }}</span>
            </div>
          }
        </div>

        <!-- Step 1: label + password -->
        @if (step === 'details') {
          <form [formGroup]="detailsForm" (ngSubmit)="submitDetails()">
            <p class="text-muted mb-3">{{ 'profile.authenticators.details_description' | translate }}</p>

            <!-- Decoy for the browser's password manager: it binds the saved login email to this
                 hidden field instead of to the name field below (autocomplete="off" is ignored). -->
            <input type="text" name="username" autocomplete="username" class="d-none" tabindex="-1" aria-hidden="true" />

            <div class="mb-3">
              <hc-form-field
                [label]="'profile.authenticators.label' | translate"
                [required]="true"
                [helper]="'profile.authenticators.label_hint' | translate"
                [error]="labelError">
                <hc-input
                  type="text"
                  formControlName="label"
                  inputId="authenticator-label"
                  name="authenticator-label"
                  autocomplete="off"
                  [maxlength]="labelMaxLength"
                  [placeholder]="'profile.authenticators.label_placeholder' | translate"
                  [hasError]="!!labelError">
                </hc-input>
              </hc-form-field>
            </div>

            <div class="mb-3">
              <hc-form-field
                [label]="'profile.authenticators.password_label' | translate"
                [required]="true"
                [error]="passwordError">
                <hc-input
                  type="password"
                  formControlName="password"
                  inputId="authenticator-password"
                  autocomplete="current-password"
                  [placeholder]="'profile.enter_password' | translate"
                  [hasError]="!!passwordError">
                </hc-input>
              </hc-form-field>
            </div>

            @if (detailsError) {
              <div class="alert alert-danger small mb-0">
                <lucide-icon name="circle-alert" class="me-1"></lucide-icon>
                {{ detailsError }}
              </div>
            }
            <!-- Hidden submit keeps Enter working inside the form -->
            <button type="submit" class="d-none" aria-hidden="true" tabindex="-1"></button>
          </form>
        }

        <!-- Step 2: scan + first code -->
        @if (step === 'scan') {
          <div class="text-center">
            <p class="text-muted mb-3">{{ 'profile.scan_qr_description' | translate }}</p>

            @if (qrCode) {
              <div class="qr-code-container mb-3">
                <div class="qr-code-svg" [innerHTML]="qrCode"></div>
              </div>
            }

            <hc-button variant="link" (clicked)="toggleManualSecret()">
              @if (showManualSecret) {
                {{ 'profile.hide_manual_code' | translate }}
              } @else {
                {{ 'profile.cant_scan' | translate }}
              }
            </hc-button>

            @if (showManualSecret && secret) {
              <div class="manual-secret-container mt-2 p-3 bg-light rounded">
                <p class="small text-muted mb-2">{{ 'profile.manual_code_instructions' | translate }}</p>
                <div class="d-flex align-items-center justify-content-center gap-2">
                  <code class="secret-code">{{ secret }}</code>
                  <hc-button variant="secondary" leftIcon="copy" (clicked)="copySecret()"></hc-button>
                </div>
              </div>
            }

            <div class="mt-4">
              <p class="text-muted mb-3">{{ 'profile.verify_code_description' | translate }}</p>

              <div class="digit-inputs-container mb-3">
                @for (digit of verificationDigits; track $index) {
                  <input
                    type="text"
                    class="digit-input"
                    [id]="'setup-digit-' + $index"
                    [value]="digit"
                    maxlength="1"
                    inputmode="numeric"
                    pattern="[0-9]"
                    autocomplete="one-time-code"
                    (input)="onDigitInput($index, $event)"
                    (keydown)="onDigitKeyDown($index, $event)"
                  />
                }
              </div>

              @if (scanError) {
                <div class="alert alert-danger small">
                  <lucide-icon name="circle-alert" class="me-1"></lucide-icon>
                  {{ scanError }}
                </div>
              }
            </div>
          </div>
        }

        <!-- Step 3: recovery codes, first authenticator only -->
        @if (step === 'recovery') {
          <app-recovery-codes-list [codes]="recoveryCodes" context="first-setup"></app-recovery-codes-list>

          <div class="alert alert-info small mt-3">
            <lucide-icon name="info" class="me-1"></lucide-icon>
            {{ 'profile.authenticators.relogin_hint' | translate }}
          </div>

          <hc-checkbox
            [label]="'profile.acknowledge_codes_saved' | translate"
            [(ngModel)]="codesAcknowledged">
          </hc-checkbox>
        }
      </div>

      <div class="modal-footer">
        @if (step === 'details') {
          <hc-button variant="secondary" size="md" (clicked)="cancel()" [disabled]="isGenerating">
            {{ 'cancel' | translate }}
          </hc-button>
          <hc-button variant="primary" size="md" [loading]="isGenerating" [disabled]="isGenerating" (clicked)="submitDetails()">
            {{ 'profile.authenticators.continue' | translate }}
          </hc-button>
        }
        @if (step === 'scan') {
          <hc-button variant="secondary" size="md" (clicked)="cancel()" [disabled]="isVerifying">
            {{ 'cancel' | translate }}
          </hc-button>
          <hc-button
            variant="primary"
            size="md"
            [disabled]="!isCodeComplete() || isVerifying"
            [loading]="isVerifying"
            (clicked)="verifyCode()">
            {{ 'profile.verify' | translate }}
          </hc-button>
        }
        @if (step === 'recovery') {
          <hc-button variant="primary" size="md" [disabled]="!codesAcknowledged" (clicked)="completeSetup()">
            {{ 'done' | translate }}
          </hc-button>
        }
      </div>
    </div>
  </div>
</div>
